<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <h3>Edit Personal Information</h3>
    </header>
    <section class="modal-card-body">
      <ValidationObserver ref="profileObserver" v-slot="{ valid }">
        <div class="columns">
          <div class="column">
            <div class="columns" style="margin-bottom: -12px">
              <div class="column">
                <h-input
                  name="fname"
                  :model="model.fname"
                  :schema="schema.fname"
                  @fname-edited="model.fname = $event"
                ></h-input>
              </div>
              <div class="column">
                <h-input
                  name="lname"
                  :model="model.lname"
                  :schema="schema.lname"
                  @lname-edited="model.lname = $event"
                ></h-input>
              </div>
            </div>
            <h-input
              name="username"
              :model="model.username"
              :schema="schema.username"
              @username-edited="model.username = $event"
            ></h-input>
            <h-input
              name="email"
              :model="model.email"
              :schema="schema.email"
              :showErrors="false"
              inputType="email"
              title="Emails can't be changed at this time"
              disabled
            ></h-input>
          </div>
          <div class="column">
            <h-upload
              name="profilePic"
              :model="model.profilePic"
              :schema="schema.profilePic"
              @profilePic-edited="model.profilePic = $event"
              :key="uploadRefresh"
            ></h-upload>
          </div>
        </div>
        <b-button
          class="is-primary is-pulled-right modal-buttons"
          style="margin-top: .6em"
          size="is-large"
          :loading="loading"
          :disabled="
            loading ||
              !valid ||
              !changed ||
              !($can($A.PATCH, model) || $can($A.PATCH, $R.USERS))
          "
          @click="updateProfile"
          >Update Profile</b-button
        >
      </ValidationObserver>
    </section>

    <footer class="modal-card-foot" style="justify-content: flex-end;"></footer>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import schema from "@/assets/schemas/fields/users.schema.js";
import Users from "@/common/resources/Users.module.js";
import { showToast } from "@/common/toast.service.js";
import { set, debounce, keys, clone } from "lodash";
import { CHECK_AUTH } from "@/store/actions.type";

export default {
  components: {
    ValidationObserver,
  },
  data() {
    return {
      schema: schema,
      changed: false,
      model: undefined,
      uploadRefresh: 1,
      loading: false,
    };
  },
  methods: {
    changedFields: async function() {
      const orig = (await Users.byID(this.model.id)).data;
      return Object.keys(this.model).filter(k => {
        const a = b => JSON.stringify(b);
        return a(orig[k]) !== a(this.model[k]) && !!this.model[k];
      });
    },
    updateProfile() {
      //Validate the input fields of the form
      this.$refs.profileObserver.validate().then(async result => {
        //If the form isn't valid, then don't make the API request
        if (!result) return;

        //Detect which fields have been changed for the PATCH request
        const updated = (await this.changedFields()).reduce(
          (a, k) => set(a, k, this.model[k]),
          { id: this.model.id },
        );

        //Set loading to true and disable the ability to cacel the modal
        this.loading = true;
        this.$parent.canCancel = false;

        //Only submit if any fields have changed
        if (Object.keys(updated).length > 1) {
          new Users(updated)
            .$update()
            .then(async res => {
              //Show success toast
              showToast("Profile updated!", "is-success");

              //Set internal model to new values
              this.model = res.data;
              this.model.profilePic = undefined;
              this.uploadRefresh++;

              //Dispatch CHECK_AUTH action to update currentUser in vuex store
              await this.$store.dispatch(CHECK_AUTH);

              //Poll API every 100ms until the user's new username is available
              for (let i = 0; i < 30; i++) {
                try {
                  const r = await Users.all({ username: this.model.username });
                  if (r.meta.count) break;

                  await new Promise(res => {
                    setTimeout(res, 100);
                  });
                } catch {
                  break;
                }
              }

              //If the user is able to cancel the modal somehow, it should navigate to the detail page of the new username
              this.$parent.onCancel = () =>
                this.$router.push(`/users/${this.model.username}`);

              //Set loading to false and enable cancelling on the modal
              this.loading = false;
              this.$parent.canCancel = true;

              //Navigate to the detail page of the new username
              this.$router.push(`/users/${this.model.username}`);
            })
            .catch(err => {
              this.loading = false;
              showToast(err.description, "is-danger");
              console.log(err);
            });
        }
      });
    },
  },
  watch: {
    model: {
      handler: debounce(
        async function() {
          this.changed = (await this.changedFields()).length;
        },
        500,
        { leading: true },
      ),
      deep: true,
    },
  },
  created() {
    const init = clone(this.$attrs.userData);
    this.model = keys(schema).reduce((a, c) => set(a, c, init[c]), init);
    this.model.profilePic = undefined;
  },
};
</script>
